import { TocBlock } from '@lighthouse/block/src/blocks'
import type { TocBlockAbstract } from '@lighthouse/core'
import type { RichTextValue } from '@lighthouse/core'
import type { TocHeading } from '@lighthouse/shared'
import {
    collectHeadingsFromJSONContent,
    findNodeDom,
    getBlockValueByContext,
    getWithScopeId,
    scroll2FlowNode,
    scrollElementIntoView
} from '@lighthouse/shared'
import { useAtomValue } from 'jotai'
import React, { memo, useCallback, useMemo } from 'react'

import { pageStackOfBlockAtom } from '@/atoms/application/state'
import { useCurrentStackIdContext } from '@/contexts/PageContext'

interface TocBlockControllerProps extends React.ComponentPropsWithoutRef<'div'> {
    blockData: TocBlockAbstract
}

const TocBlockController: React.FC<TocBlockControllerProps> = ({ blockData, ...rest }) => {
    const { targetBlockId, targetScope, maxDepth = 3, offsetY = 0 } = blockData.config
    const stackId = useCurrentStackIdContext()
    const stackMap = useAtomValue(pageStackOfBlockAtom)

    const headings = useMemo(() => {
        if (!targetBlockId) return []

        const blockKey = getWithScopeId(targetBlockId, targetScope)
        const item = getBlockValueByContext(stackMap, { stackId, blockId: blockKey })

        if (item?.blockType !== 'field' || item.type !== 'notes') return []
        const richText = item.value as RichTextValue
        if (!richText) return []

        const allHeadings = collectHeadingsFromJSONContent(richText)
        return maxDepth < 3 ? allHeadings.filter(h => h.level <= maxDepth) : allHeadings
    }, [stackMap, stackId, targetBlockId, targetScope, maxDepth])

    const handleHeadingClick = useCallback(
        (heading: TocHeading) => {
            if (!targetBlockId) return

            scroll2FlowNode(targetBlockId, targetScope, { offset: { y: offsetY } })

            requestAnimationFrame(() => {
                const blockDom = findNodeDom({ id: targetBlockId, scope: targetScope })
                const headingEls = blockDom?.querySelectorAll('h1, h2, h3')
                const headingEl = headingEls?.[heading.index] as HTMLElement | undefined
                if (headingEl) scrollElementIntoView(headingEl, { offset: { y: offsetY } })
            })
        },
        [targetBlockId, targetScope, offsetY]
    )

    return (
        <TocBlock
            blockData={blockData}
            headings={headings}
            onHeadingClick={handleHeadingClick}
            {...rest}
        />
    )
}

export default memo(TocBlockController)
